import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { BellRing } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];

const INTERESTS = [
  "Nursing Algebra",
  "Leadership",
  "Math Tutoring",
  "Math Tutoring Bundle (5 sessions)",
  "Books",
  "Art",
  "Not sure yet",
];

const EMPTY = { name: "", email: "", interest: "", message: "" };

export const WaitlistSection = () => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/waitlist", form);
      setDone(true);
      setForm(EMPTY);
      toast.success("You're on the waiting list.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not join right now.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid="waitlist-section"
      className="relative overflow-hidden border-t border-white/10 bg-[#050505] px-6 md:px-12 py-24 md:py-28"
    >
      <div className="pointer-events-none absolute -left-32 top-1/2 h-[360px] w-[360px] -translate-y-1/2 rounded-full bg-[#7C3AED]/15 blur-[100px]" />
      <div className="relative mx-auto grid max-w-[1200px] gap-12 md:grid-cols-2 md:items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE }}
        >
          <span className="text-xs uppercase tracking-[0.4em] text-[#7C3AED]">Coming Soon</span>
          <h2 className="mt-4 font-display text-4xl uppercase tracking-tight text-white sm:text-5xl">
            Join the Waiting List
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-white/60 md:text-lg">
            Online checkout is opening soon. Leave your name and email, tell us what you're
            interested in, and Dr. Louden will personally email you the moment ordering opens —
            no payment needed today.
          </p>
          <div className="mt-8 flex items-center gap-3 text-white/50">
            <BellRing className="h-5 w-5 text-[#7C3AED]" />
            <span className="text-sm">Waiting list members hear first — courses, books, and art.</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
        >
          {done ? (
            <div
              data-testid="waitlist-success"
              className="flex min-h-[280px] flex-col items-start justify-center rounded-2xl border border-[#7C3AED]/40 bg-[#7C3AED]/10 p-10"
            >
              <div className="font-display text-3xl uppercase text-white">You're on the list.</div>
              <p className="mt-4 text-white/60">
                Watch your inbox — you'll be the first to know when ordering opens.
              </p>
              <button
                data-testid="waitlist-reset-btn"
                onClick={() => setDone(false)}
                className="magnetic-btn mt-8 rounded-full border border-white/30 px-6 py-2.5 text-sm uppercase tracking-wider text-white hover:bg-white hover:text-black"
              >
                Add someone else
              </button>
            </div>
          ) : (
            <form
              data-testid="waitlist-form"
              onSubmit={submit}
              className="space-y-6 rounded-2xl border border-white/10 bg-[#0D0D12] p-8"
            >
              <input
                data-testid="waitlist-name"
                className="line-input"
                placeholder="Full name"
                value={form.name}
                onChange={set("name")}
              />
              <input
                data-testid="waitlist-email"
                type="email"
                className="line-input"
                placeholder="Email address"
                value={form.email}
                onChange={set("email")}
              />
              <select
                data-testid="waitlist-interest"
                className="line-input cursor-pointer"
                value={form.interest}
                onChange={set("interest")}
              >
                <option value="" disabled className="bg-[#111] text-white/50">
                  I'm interested in…
                </option>
                {INTERESTS.map((i) => (
                  <option key={i} value={i} className="bg-[#111] text-white">
                    {i}
                  </option>
                ))}
              </select>
              <textarea
                data-testid="waitlist-message"
                className="line-input resize-none"
                rows={2}
                placeholder="Anything you'd like us to know (optional)"
                value={form.message}
                onChange={set("message")}
              />
              <button
                data-testid="waitlist-submit-btn"
                type="submit"
                disabled={submitting}
                className="magnetic-btn w-full rounded-full bg-[#7C3AED] px-8 py-4 text-sm font-semibold uppercase tracking-wider text-white hover:bg-white hover:text-black disabled:opacity-50"
              >
                {submitting ? "Joining…" : "Reserve My Spot"}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default WaitlistSection;
